import { IEventSystem } from "@dhx/ts-common/events";
import { Navbar } from "./Navbar";
import { button } from "./elements/button";
import { navItem } from "./elements/navItem";
import { customHTMLButton } from "./elements/customHTMLButton";
import { imageButton } from "./elements/imageButton";
import { input } from "./elements/input";
import { menuItem } from "./elements/menuItem";
import { separator } from "./elements/separator";
import { spacer } from "./elements/spacer";
import { title } from "./elements/title";
import { IItem, ItemType, NavigationBarEvents } from "./types";
import { navbarComponentMixin } from "./elements/helpers";
import { TreeCollection } from "@dhx/ts-data";

function itemfactory(item: IItem, events: IEventSystem<NavigationBarEvents>, widgetName: string, props: IFactoryProps) {
	switch (item.type as ItemType) {
		case ItemType.navItem:
		case ItemType.selectButton:
			return navItem(item, widgetName, props.collapsed);
		case ItemType.button:
			return button(item, widgetName);
		case ItemType.title:
			return title(item, widgetName);
		case ItemType.separator:
			return separator(item, widgetName);
		case ItemType.spacer:
			return spacer(item, widgetName);
		case ItemType.input:
			return input(item, events, widgetName);
		case ItemType.imageButton:
			return imageButton(item, widgetName);
		case ItemType.menuItem:
			return menuItem(item, widgetName, props.asMenuItem);
		case ItemType.customHTMLButton:
			return customHTMLButton(item, widgetName, props.asMenuItem);
		case ItemType.block:
		default:
			throw new Error("unknown item type " + item.type);
	}
}

interface IFactoryConfig<T extends Navbar> {
	defaultType: ItemType;
	allowedTypes: ItemType[];
	widgetName: string;
	widget: T;
}

interface IFactoryProps {
	asMenuItem: boolean;
	collapsed?: boolean;
}

export function createFactory<T extends Navbar>({ defaultType, allowedTypes, widgetName, widget }: IFactoryConfig<T>): (item: IItem, asMenuItem?: boolean) => any {
	const allowedSet = new Set();
	for (const type of allowedTypes) {
		allowedSet.add(type);
	}
	const { config, events, data } = widget;
	return function (item: IItem, asMenuItem?: boolean) {
		if (item.hidden) {
			return null;
		}
		if (!item.type || item.type === "button" || item.type === "navItem" || item.type === "menuItem") {
			if (!item.value && !item.icon && !item.html) {
				return null;
			}
		}
		item.type = item.type || defaultType;
		if (allowedSet && !allowedSet.has(item.type)) {
			item.type = defaultType;
		}
		if (item.type === ItemType.imageButton && widgetName !== "ribbon") {
			item.active = false;
		}
		if (asMenuItem &&
			item.type !== ItemType.spacer &&
			item.type !== ItemType.separator &&
			item.type !== ItemType.customHTML) {
			item.type = ItemType.menuItem;
		}
		if (data.haveItems(item.id)) {
			normalizeOpenIcon(widgetName, item, data);
		}

		const itemVNode = item.type !== ItemType.customHTML &&
			itemfactory(item, events, widgetName, { asMenuItem, collapsed: widgetName !== "sidebar" || (config as any).collapsed });
		return navbarComponentMixin(widgetName, item, asMenuItem, itemVNode);
	};
}

function normalizeOpenIcon<T extends IItem>(widgetName: string, item: T, data: TreeCollection<T>): void {
	switch (widgetName) {
		case "sidebar":
		case "context-menu":
			item.$openIcon = "right";
			break;
		case "toolbar":
			if (item.parent === data.getRoot()) {
				item.$openIcon = "right";
			} else {
				item.$openIcon = "bot";
			}
			break;
		case "menu":
			if (item.parent !== this.data.getRoot()) {
				item.$openIcon = "right";
			}
			break;
		case "ribbon":
			const parent = data.getItem(item.parent);
			if (parent && item.type !== ItemType.block) {
				if (parent.type === ItemType.block) {
					item.$openIcon = "bot";
				} else {
					item.$openIcon = "right";
				}
			}
			break;
	}
}